import { FC, useEffect, useMemo, useState } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { convertsCentsToCurrency } from '@npi/buddy-utils';

import { NPIDataGrid } from 'src/components/NPIDataGrid/NPIDataGrid';
import { createColumnDef } from 'src/components/NPIDataGrid/NPIDataGrid.utils';
import ReusableGrid from 'src/components/ReusableGrid';
import { DEFAULT_BORDER } from 'src/constants';
import useGeneratePresignedUrl from 'src/hooks/useGeneratePresignedUrl';

interface OrderItem {
    imageUrl: string;
    description: string;
    quantity: number;
    monthlyPrice: number;
    terminalId: string;
}

interface DisplayConfig {
    showMerchantAgreement: boolean;
}

interface ServiceDetailsSectionProps {
    contractUrl?: string | null;
    orderPrimary?: OrderItem[];
    orderSecondary?: OrderItem[];
    terminalId?: string;
    orderPrimaryUrls?: { imageUrl: string }[];
    orderSecondaryUrls?: { imageUrl: string }[];
    displayConfig?: DisplayConfig;
}

const ServiceDetailsSection: FC<ServiceDetailsSectionProps> = ({
    contractUrl,
    terminalId,
    orderPrimary = [],
    orderSecondary = [],
    orderPrimaryUrls = [],
    orderSecondaryUrls = [],
    displayConfig = {
        showMerchantAgreement: true,
    },
}) => {
    const handleViewAgreement = () => {
        /* TODO*/
    };

    const { validateAwsURL } = useGeneratePresignedUrl();
    const [productImages, setProductImages] = useState<Record<number, string>>({});

    useEffect(() => {
        const fetchPresignedUrls = async () => {
            const urlMap: Record<number, string> = {};
            const all = [...orderPrimaryUrls, ...orderSecondaryUrls];

            for (let i = 0; i < all.length; i++) {
                const url = await validateAwsURL(all[i].imageUrl);
                urlMap[i + 1] = url ?? all[i].imageUrl;
            }

            setProductImages(urlMap);
        };

        fetchPresignedUrls();
    }, [orderPrimaryUrls, orderSecondaryUrls]);

    const rows = useMemo(() => {
        const all = [...orderPrimary, ...orderSecondary];
        return all.map((o, idx) => {
            return {
                id: idx + 1,
                productImg: productImages[idx + 1] ?? '',
                description: o.description,
                qty: o.quantity,
                monthly: `€ ${convertsCentsToCurrency(o.monthlyPrice) ?? '0'}`,
                tid: terminalId ?? '',
            };
        });
    }, [orderPrimary, orderSecondary, productImages]);

    const columns = [
        createColumnDef({
            field: 'productImg',
            headerName: 'Product',
            renderCell: ({ value }) => {
                return <img src={value} alt="product" style={{ width: 40, height: 40 }} />;
            },
        }),
        createColumnDef({ field: 'description', headerName: 'Description' }),
        createColumnDef({ field: 'qty', headerName: 'QTY' }),
        createColumnDef({ field: 'monthly', headerName: 'Monthly Rental' }),
        createColumnDef({ field: 'tid', headerName: 'TID' }),
    ];

    const { showMerchantAgreement = true } = displayConfig;

    return (
        <Box mt={4} data-testid="service-details-section">
            {showMerchantAgreement && (
                <Box
                    sx={{
                        ...DEFAULT_BORDER,
                        height: 116,
                        px: 3,
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: '#FFFFFF',
                        boxShadow: '0px 4px 20px rgba(38,50,56,0.1)',
                    }}
                >
                    <Typography flex={1} fontSize={21} fontWeight={500}>
                        Merchant’s Card Service Agreement
                    </Typography>

                    <Button
                        variant="npi-primary"
                        disabled={!contractUrl}
                        onClick={handleViewAgreement}
                        isElevationDisabled
                    >
                        View Agreement
                    </Button>
                </Box>
            )}

            <Box mt={showMerchantAgreement ? 5 : 0}>
                <ReusableGrid title="Products / Services" xs={12}>
                    <Box
                        sx={{
                            width: '100%',
                            '& .MuiDataGrid-toolbarContainer': { display: 'none' },
                        }}
                    >
                        <NPIDataGrid
                            rows={rows}
                            columns={columns}
                            getRowId={(row) => {
                                return row.id;
                            }}
                            dataTestId="checkin-services"
                        />
                    </Box>
                </ReusableGrid>
            </Box>
        </Box>
    );
};

export default ServiceDetailsSection;
